import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { HomePath } from '../../shared/constants/routes';
import { DrawerService } from '../drawer/drawer.service';

interface LeftNavItem {
  readonly label: string;
  readonly route: HomePath;
  readonly icon: string;
  readonly visible: boolean;
}

interface LeftNavSection {
  readonly label: string;
  readonly items: ReadonlyArray<LeftNavItem>;
}

@Component({
  selector: 'app-left-sidenav',
  templateUrl: './left-sidenav.component.html',
  styleUrls: ['./left-sidenav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, MatTooltipModule, MatIconButton, AsyncPipe, MatListModule, MatDividerModule],
})
export class LeftSidenavComponent {
  private readonly drawerService = inject(DrawerService);
  private readonly router = inject(Router);

  readonly isCollapsed$ = this.drawerService.leftNavCollapsed$;
  readonly currentPrimaryPath = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.getCurrentPrimaryPath()),
      startWith(this.getCurrentPrimaryPath())
    ),
    { initialValue: this.getCurrentPrimaryPath() }
  );
  readonly myTodosItem: LeftNavItem = { label: 'My To-Dos', route: HomePath.todos, icon: 'check_circle', visible: true };
  readonly navSections: ReadonlyArray<LeftNavSection> = [
    {
      label: 'RCSA',
      items: [
        { label: 'RCSAs', route: HomePath.rcsas, icon: 'fact_check', visible: true },
        { label: 'Controls', route: HomePath.controls, icon: 'control_camera', visible: true },
        { label: 'Issues', route: HomePath.issues, icon: 'build_circle', visible: true },
        { label: 'Events', route: HomePath.events, icon: 'crisis_alert', visible: true },
      ],
    },
    {
      label: 'Policies',
      items: [{ label: 'Standards', route: HomePath.standards, icon: 'policy', visible: true }],
    },
    {
      label: 'Risk & Control Library',
      items: [
        {
          label: 'Controls',
          route: HomePath.libraryControls,
          icon: 'library_books',
          visible: environment.libraryControlsEnabled,
        },
        {
          label: 'Audit Log',
          route: HomePath.auditLog,
          icon: 'history',
          visible: false,
        },
      ],
    },
  ];

  toggleCollapse(): void {
    this.drawerService.toggleLeftNavCollapsed();
  }

  onNavLinkClick(event: Event, route: HomePath): void {
    event.preventDefault();
    event.stopPropagation();

    this.router.navigateByUrl(`/${route}`);
  }

  private getCurrentPrimaryPath(): string {
    const primarySegments = this.router.parseUrl(this.router.url).root.children['primary']?.segments ?? [];
    const firstSegment = primarySegments[0]?.path;
    return firstSegment ? `/${firstSegment}` : '/';
  }
}
